import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import styled from 'styled-components';

import ChatDataDisplayContent from 'components/customer/conversation_history/conversation_items_v2/chat/chat_data_display_content';
import ChatHTMLContent from 'components/customer/conversation_history/conversation_items_v2/chat/chat_html_content';
import ChatTextContent from 'components/customer/conversation_history/conversation_items_v2/chat/chat_text_content';
import ChatMessage from 'models/chat_message';
import { ChatMessageType } from 'models/chat_message_constants';
import ConversationItem from 'models/conversation_item';
import ChatTemplatedCardContent from 'components/customer/conversation_history/conversation_items_v2/chat/chat_flexible_card_content';
import ErrorReporter from 'scripts/infrastructure/error_reporter';
import { isDevelopment } from 'scripts/application/lib/environment';

export default function CompositeMessageItemContent({ item }) {
  const messageParts = item.content.messageData.messages;
  if (isEmpty(messageParts)) return null;

  const nestedMessages = messageParts.reduce((acc, part, idx) => {
    /**
     * Try to "rehydrate" the message part as if it was a full message. Since the
     * parts come in the context of the wrapping Composite Message, all envelope
     * fields of the Composite Message (i.e. `initiator`, `customerId` etc.) will
     * "project" onto the nested message part
     */
    const chatContent = ChatMessage.create(part);
    const nestedConversationMessage = ConversationItem.create({
      ...item,
      id: `${item.id}_${idx}`,
      content: chatContent,
    });
    const rendered = renderNestedMessage(nestedConversationMessage, idx);
    if (rendered) acc.push(rendered);

    return acc;
  }, []);
  return <div>{nestedMessages}</div>;
}

/**
 * Render an individual nested message.
 */
function renderNestedMessage(nestedMessage, key) {
  const { messageType } = nestedMessage.content || {};
  const devMode = isDevelopment();

  let renderedContent;
  switch (messageType) {
    case ChatMessageType.DATA_DISPLAY:
      renderedContent = <ChatDataDisplayContent item={nestedMessage} withoutBubble />;
      break;

    case ChatMessageType.FLEXIBLE_CARD:
      renderedContent = <ChatTemplatedCardContent item={nestedMessage} withoutBubble />;
      break;

    case ChatMessageType.HTML:
      renderedContent = <ChatHTMLContent item={nestedMessage} withoutAttachments withoutBubble />;
      break;

    case ChatMessageType.TEXT:
      renderedContent = <ChatTextContent item={nestedMessage} withoutAttachments withoutBubble />;
      break;

    default: {
      renderedContent = null;

      // Report the fact that we can't render the nested message
      const unknownItemErr = new Error(`could not render nested message of type [${messageType}]`);
      ErrorReporter.reportError(unknownItemErr, {
        message: 'Unexpected nested message type. Ignoring',
      });
      if (!devMode) break;

      // Only throw in dev so that the tests would catch the error
      throw unknownItemErr;
    }
  }

  return renderedContent ? <MessagePartWrapper key={key}>{renderedContent}</MessagePartWrapper> : null;
}

CompositeMessageItemContent.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

const MessagePartWrapper = styled.div`
  margin-top: 4px;

  & + & {
    margin-top: 1em;
  }
`;
